import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

/* ✅ STATIC REVIEWS (NO BACKEND) */
const STATIC_REVIEWS = [
  {
    id: 1,
    name: "Simran Kaur",
    rating: 5,
    text: "Amazing service and super friendly staff. Loved my haircut!",
    created_at: "2024-10-01",
  },
  {
    id: 2,
    name: "Harpreet Singh",
    rating: 5,
    text: "Very professional team and great ambience. Highly recommended.",
    created_at: "2024-09-18",
  },
  {
    id: 3,
    name: "Jasleen Kaur",
    rating: 4,
    text: "Excellent facial service. Skin felt fresh and glowing.",
    created_at: "2024-08-25",
  },
  {
    id: 4,
    name: "Gurpreet Kaur",
    rating: 5,
    text: "Best salon experience in Amritsar. Worth every rupee!",
    created_at: "2024-07-12",
  },
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ✅ STATIC LOAD (NO API CALLS) */
  useEffect(() => {
    setReviews(STATIC_REVIEWS);
    setLoading(false);
  }, []);

  const avgRating =
    reviews.length > 0
      ? (
          reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length
        ).toFixed(1)
      : "0.0";

  return (
    <div className="pt-24" data-testid="reviews-page">
      {/* Header */}
      <section className="section-padding py-16 bg-obsidian text-white text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">
            Testimonials
          </p>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-4">
            Client Reviews
          </h1>
          <p className="text-white/70 max-w-xl mx-auto">
            Hear what our valued clients have to say
          </p>
        </motion.div>
      </section>

      {/* Rating Summary */}
      <section className="section-padding py-12 bg-white border-b">
        <div className="flex flex-col items-center justify-center gap-6">
          <p className="font-heading text-6xl text-gold">{avgRating}</p>
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={22}
                className={
                  i < Math.round(avgRating)
                    ? "fill-gold text-gold"
                    : "text-neutral-300"
                }
              />
            ))}
          </div>
          <p className="text-neutral-500">
            {reviews.length} happy clients
          </p>
        </div>
      </section>

      {/* Reviews List */}
      <section className="section-padding section-gap">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {(Array.isArray(reviews) ? reviews : []).map((review, index) => (
              <motion.div
                key={review.id ?? index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-neutral-50 p-8 md:p-10 border border-neutral-100 rounded-sm"
                data-testid={`review-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={18}
                      className={
                        i < review.rating
                          ? "fill-gold text-gold"
                          : "text-neutral-300"
                      }
                    />
                  ))}
                </div>

                <p className="text-neutral-700 italic text-lg leading-relaxed mb-6">
                  “{review.text}”
                </p>

                <div className="flex items-center justify-between">
                  <p className="font-medium text-obsidian">{review.name}</p>
                  <p className="text-xs text-neutral-400">
                    {new Date(review.created_at).toLocaleDateString("en-IN", {
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Why Choose Us */}
      <section className="section-padding py-20 bg-neutral-50">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">
            Why Choose Us
          </p>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
            What People Love
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {[
            "Expert Stylists",
            "Premium Products",
            "Luxury Experience",
          ].map((title, i) => (
            <div key={i} className="text-center p-6">
              <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="text-gold" size={28} />
              </div>
              <h3 className="font-heading text-xl mb-2">{title}</h3>
              <p className="text-neutral-500 text-sm">
                Trusted by hundreds of satisfied clients
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
